import { Modal } from "react-bootstrap";
import { faFloppyDisk, faXmark, faListCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormik } from "formik";
import PositionDomain from "@serie3/freelancesplaceapi/Domain/Billing/Outgoing/Position";
import PositionForm from "@serie3/freelancesplaceapi/Form/Billing/PositionForm";
import Position from "@serie3/freelancesplaceapi/Entity/Billing/Position";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1px solid #e0e0e0",
  borderRadius: "8px", fontSize: "0.92rem", color: "#212121",
  background: "#fff", outline: "none", boxSizing: "border-box",
  fontFamily: "inherit",
};

const labelStyle = {
  fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.08em", color: "#9e9e9e", marginBottom: "6px", display: "block",
};

const Field = ({ label, error, children }) => (
  <div style={{ marginBottom: "16px" }}>
    <label style={labelStyle}>{label}</label>
    {children}
    {error && <div style={{ fontSize: "0.78rem", color: "#c62828", marginTop: "4px" }}>{error}</div>}
  </div>
);

const ReadonlyAmount = ({ label, value, accent }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "8px 0", borderTop: accent ? "2px solid #e0e0e0" : "1px solid #f0f0f0",
  }}>
    <span style={{ fontSize: "0.85rem", fontWeight: 500, color: "#9e9e9e" }}>{label}</span>
    <span style={{ fontSize: accent ? "1rem" : "0.9rem", fontWeight: accent ? 800 : 600, color: accent || "#424242" }}>
      € {value}
    </span>
  </div>
);

const PositionModal = ({ show, setShow, financeId, position, dataList, setDataFunction, showErrorFunction }) => {
  const editPositionForm = PositionForm.createPositionForm(dataList, setDataFunction, showErrorFunction, setShow);
  const formik = useFormik(editPositionForm);
  const isNew = !position;
  const title = isNew ? "Add Position" : "Edit Position";

  return (
    <>
      <style>{`
        .position-modal {
          border-radius: 14px !important; overflow: hidden !important;
          border: none !important; box-shadow: 0 8px 40px rgba(0,0,0,0.18) !important;
        }
      `}</style>
      <Modal size="lg" show={show} onHide={() => setShow(false)}
        contentClassName="position-modal"
        onShow={() => {
          position ? formik.setValues(PositionForm.setEditData(position)) : formik.setValues(Position);
          formik.setFieldValue("finanz_vorgang", { id: financeId });
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          {/* Header */}
          <div style={{
            background: "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)",
            padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{
                width: "32px", height: "32px", borderRadius: "8px",
                background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center",
                justifyContent: "center", color: "#fff", fontSize: "0.9rem",
              }}>
                <FontAwesomeIcon icon={faListCheck} />
              </div>
              <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>{title}</span>
            </div>
            <button type="button" onClick={() => setShow(false)} style={{
              background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px",
              width: "30px", height: "30px", display: "flex", alignItems: "center",
              justifyContent: "center", cursor: "pointer", color: "#fff",
            }}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          {/* Body */}
          <div style={{ background: "#f8f9fb", padding: "24px" }}>

            <Field label="Title" error={formik.errors.titel}>
              <input type="text" name="titel" placeholder="Position title"
                value={formik.values.titel} onChange={formik.handleChange}
                style={{ ...inputStyle, borderColor: formik.errors.titel ? "#c62828" : "#e0e0e0" }} />
            </Field>

            <Field label="Description">
              <textarea name="beschreibung" placeholder="Description" rows={3}
                value={formik.values.beschreibung} onChange={formik.handleChange}
                style={{ ...inputStyle, resize: "vertical" }} />
            </Field>

            {/* Price / Amount / Tax */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px", marginBottom: "16px" }}>
              <Field label="Price (€)" error={formik.errors.preis}>
                <input type="text" name="preis" placeholder="0.00"
                  value={formik.values.preis}
                  onChange={(e) => { formik.handleChange(e); PositionDomain.handlePositionFormChange(e, formik); }}
                  style={{ ...inputStyle, borderColor: formik.errors.preis ? "#c62828" : "#e0e0e0" }} />
              </Field>
              <Field label="Amount" error={formik.errors.multiplikator}>
                <input type="number" name="multiplikator" placeholder="1"
                  value={formik.values.multiplikator}
                  onChange={(e) => { formik.handleChange(e); PositionDomain.handlePositionFormChange(e, formik); }}
                  style={{ ...inputStyle, borderColor: formik.errors.multiplikator ? "#c62828" : "#e0e0e0" }} />
              </Field>
              <Field label="Tax (%)" error={formik.errors.steuersatz}>
                <input type="number" name="steuersatz" placeholder="0"
                  value={formik.values.steuersatz}
                  onChange={(e) => { formik.handleChange(e); PositionDomain.handlePositionFormChange(e, formik); }}
                  style={{ ...inputStyle, borderColor: formik.errors.steuersatz ? "#c62828" : "#e0e0e0" }} />
              </Field>
            </div>

            {/* Calculated totals */}
            <div style={{
              background: "#fff", borderRadius: "10px", padding: "12px 16px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}>
              <ReadonlyAmount label="Net" value={formik.values.gesamt_preis} />
              <ReadonlyAmount label="Tax" value={formik.values.taxabsolute} />
              <ReadonlyAmount label="Gross" value={formik.values.gross_price} accent="#2e7d32" />
            </div>
          </div>

          {/* Footer */}
          <div style={{
            background: "#fff", borderTop: "1px solid #f0f0f0",
            padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: "8px",
          }}>
            <button type="button" onClick={() => setShow(false)} style={{
              padding: "8px 16px", background: "#f5f5f5", color: "#424242",
              border: "none", borderRadius: "8px", fontWeight: 600,
              fontSize: "0.88rem", cursor: "pointer",
            }}>
              Cancel
            </button>
            <button type="submit" style={{
              display: "inline-flex", alignItems: "center", gap: "7px",
              padding: "8px 18px", background: "#2e7d32", color: "#fff",
              border: "none", borderRadius: "8px", fontWeight: 600,
              fontSize: "0.88rem", cursor: "pointer",
              boxShadow: "0 2px 8px rgba(46,125,50,0.25)",
            }}>
              <FontAwesomeIcon icon={faFloppyDisk} /> Save
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default PositionModal;
